import { useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import InsightContactDrawer from '../../components/InsightContactDrawer';
import {
  getDefaultValues,
  preProcessRequestData,
  preProcessSubmitData,
} from '../../lib';
import { createSchema } from '../../schemas';
import validationSchema from './validations';

const FORM_ID = 'INSIGHT_CONTACT_CREATE_FORM';

function InsightContactCreate({ open, onClose, commonParams }) {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const snackbar = useSnackbar();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post } = useDataProvider(provider);

  const fetchDefaultValues = useCallback(async () => {
    return {
      data: getDefaultValues(commonParams.insightPurchased),
      errors: null,
    };
  }, [commonParams.insightPurchased]);

  const onSubmit = async data => {
    try {
      const response = await post(
        commonParams.phonesUrl,
        preProcessSubmitData(data),
      );

      if (response?.ok) {
        const warnings = response.data?.warnings;
        const hasWarnings = Array.isArray(warnings) && warnings.length > 0;

        if (hasWarnings) {
          snackbar.showMessage({
            type: 'warning',
            data: formatMessage(commonParams.phonesSaveWarning),
          });
        } else {
          snackbar.showMessage(formatMessage(commonParams.phonesSaveSuccess));
        }

        return {
          nextValues: {
            ...preProcessRequestData(response?.data),
            insightPurchased: commonParams.insightPurchased,
          },
          errors: null,
          callback: () => {
            commonParams.refreshTable();
            onClose();
          },
        };
      }

      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });

      return {
        nextValues: data,
        errors: response?.errors,
      };
    } catch (err) {
      return {
        nextValues: data,
        errors: [{ message: err.message }],
      };
    }
  };

  return (
    <InsightContactDrawer
      formId={FORM_ID}
      formRef={formRef}
      title={formatMessage({
        id: 'ADD_CONTACT',
        defaultMessage: 'Add contact',
      })}
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      schema={createSchema}
      validationSchema={validationSchema(formatMessage)}
      fetchDefaultValues={fetchDefaultValues}
    />
  );
}

export default observer(InsightContactCreate);
